import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import * as XLSX from "xlsx";
import {
  Alert,
  Container,
  Box,
  Tabs,
  Tab,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Typography,
  Chip,
  Stack,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  CircularProgress,
  TextField,
  InputAdornment,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import {
  Eye,
  MoreVertical,
  CheckCircle,
  Pencil,
  Printer,
  UserCheck,
  Search,
  Download,
  Mail, // <-- Added Mail icon
} from "lucide-react";

import useApiEp from "../../hooks/useApiEp";
import MuPb from "../../widgets/MuPb";
import { getErrorResponse } from "../../utils/util";
import { API_ENDPOINT, USER_ROLES } from "../../utils/constants";
import { getHeader } from "../../utils/utils2";
import {
  printRow,
  printAll,
} from "../../dashboard/onsitemodule/ImtexBadgeDimension";
import { ExportContext } from "../../context/ExportProvider";
import { toast } from "react-toastify";
import { useDialog } from "../dialogs/DialogProvider";
import { DIALOG_TYPES } from "../dialogs/CommonDialog";

const ACTION_TYPES = {
  TRADE_DELEGATE: "td",
  SPECIAL_INVITEE: "si",
  VISITOR: "vi",
};

const UPDATE_TYPE_INFO = {
  [ACTION_TYPES.SPECIAL_INVITEE]: {
    label: "Special Invitee",
    icon: <Printer size={22} />,
    description: "Email with QR will be sent to this person.",
  },
  [ACTION_TYPES.VISITOR]: {
    label: "Visitor",
    icon: <UserCheck size={22} />,
    description:
      "The visitor will receive a confirmation email and will get access to their dashboard once they set a password.",
  },
};

const SUCCESS_MESSAGES = {
  [ACTION_TYPES.TRADE_DELEGATE]:
    "A confirmation email has been sent to the delegate with an option to activate their account and log in to connect with exhibitors.",
  [ACTION_TYPES.SPECIAL_INVITEE]:
    "This registration has been marked as a Special Invitee. No email is sent — please print and hand over the badge from your end.",
  [ACTION_TYPES.VISITOR]:
    "This registration has been approved as a Visitor. A confirmation email has been sent, and they'll get dashboard access after setting a password.",
};

const getDelegateName = (row) =>
  `${row?.title ? row.title + " " : ""}${row?.firstName || ""} ${
    row?.lastName || ""
  }`.trim() || "N/A";

const TabLabel = ({ label, count, color }) => (
  <Stack direction="row" spacing={1} alignItems="center">
    <span>{label}</span>
    {count !== undefined && count !== null && (
      <Chip
        size="small"
        label={count}
        color={color}
        sx={{ height: 20, fontSize: "0.75rem" }}
      />
    )}
  </Stack>
);

const formatDataForExcel = (dataList) => {
  return dataList.map((row) => {
    const rawCats =
      row.selectProductCategoriesChooseMin10Products ||
      row.selectProductCategories ||
      row.productCategories ||
      row.selectedCategories ||
      [];
    const categoryList = Array.isArray(rawCats)
      ? rawCats
      : Array.isArray(rawCats?.value)
      ? rawCats.value
      : [];
    const categories = categoryList
      .map((c) => {
        if (typeof c === "string") return c;
        if (c && typeof c === "object") {
          const parts = [
            c.MainCategory || c.mainCat,
            c.Category || c.subCat,
            c.SubCategory || c.subSubCat,
          ].filter(Boolean);
          if (parts.length > 0) return parts.join(" > ");
          return c.name || c.label || "";
        }
        return "";
      })
      .filter(Boolean)
      .join(" | ");

    return {
      Username: row.username || "",
      "User ID": row.user_id || "",
      "Barcode ID": row.barcode_id || "",
      Title: row.title || "",
      "First Name": row.firstName || "",
      "Last Name": row.lastName || "",
      Designation: row.designation || row.contactDesignation?.value || row.role || "",
      "Mobile No": row.mobileNo || row.mobile || row.phone?.value || row.phone || row.phoneNo || row.contactNo || "",
      Email: row.email || row.contactEmail?.value || "",
      "Company Name": (typeof row.companyName === "string" ? row.companyName : row.companyName?.value) || row.company || row.institute || row.institution || row.organization || "",
      Address: row.address || "",
      Country: row.country?.name || "",
      State: row.state?.name || "",
      City: row.city?.name || "",
      "Pin/Zip": row.pinzip || "",
      "Industry Sector": row.industrySector || "",
      Department: row.department || "",
      "Invitation From": row.invitationReceivedFrom || "",
      "Selected Product Categories": categories,
      "Visitor Type": row.eventVisitor || "",
      "QR Link": row.qrLink || "",
    };
  });
};

const ManageDelegates = () => {
  const navigate = useNavigate();
  const { showDialog } = useDialog();
  const { exportVisitors, visitorExporting } = useContext(ExportContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") === "1" ? 1 : 0;

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  // Search & Filter State
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");

  // Checkbox State
  const [selectedRows, setSelectedRows] = useState([]);

  // Dialog & Menu States
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuRow, setMenuRow] = useState(null);
  const isMenuOpen = Boolean(menuAnchorEl);

  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [approveTargetRow, setApproveTargetRow] = useState(null);

  const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
  const [updateTargetRow, setUpdateTargetRow] = useState(null);
  const [selectedUpdateType, setSelectedUpdateType] = useState(null);

  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState(null);

  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const currentStatus = activeTab === 0 ? "false" : "true";

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput);
      setPage(0);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Construct query url with optional role filter
  let apiUrl = `/event/get-td-for-action?status=${currentStatus}&page=${
    page + 1
  }&limit=${rowsPerPage}&search=${encodeURIComponent(debouncedSearch)}`;

  if (activeTab === 1 && roleFilter !== "all") {
    apiUrl += `&role=${encodeURIComponent(roleFilter)}`;
  }

  const { data, isLoading, isError, error } = useApiEp(apiUrl);

  const { data: pendingCountData } = useApiEp(
    `/event/get-td-for-action?status=false&page=1&limit=1`
  );
  const { data: approvedCountData } = useApiEp(
    `/event/get-td-for-action?status=true&page=1&limit=1`
  );

  const handleTabChange = (event, newValue) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("tab", String(newValue));
        return next;
      },
      { replace: false }
    );
    setPage(0);
    setSelectedRows([]);
    setRoleFilter("all"); // Reset role filter when switching tabs
  };

  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewDetails = (id) => {
    if (activeTab === 0) {
      navigate(`/visitor-view/${id}?pendingApproval=true`);
    } else {
      navigate(`/visitor-view/${id}`);
    }
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = [...selectedRows];
      data?.data?.forEach((row) => {
        if (!newSelecteds.some((r) => r._id === row._id)) {
          newSelecteds.push(row);
        }
      });
      setSelectedRows(newSelecteds);
      return;
    }
    const currentPageIds = data?.data?.map((row) => row._id) || [];
    setSelectedRows(
      selectedRows.filter((r) => !currentPageIds.includes(r._id))
    );
  };

  const handleClickCheckbox = (event, row) => {
    const isSelected = selectedRows.some((r) => r._id === row._id);
    if (isSelected) {
      setSelectedRows(selectedRows.filter((r) => r._id !== row._id));
    } else {
      setSelectedRows([...selectedRows, row]);
    }
  };

  const isSelected = (id) => selectedRows.some((r) => r._id === id);

  const handlePrintBadge = (row) => printRow(row);
  const handlePrintSelected = () => {
    if (selectedRows.length > 0) {
      printAll(selectedRows);
    } else if (data?.data?.length > 0) {
      printAll(data.data);
    }
  };

  const handleExport = async () => {
    exportVisitors("approvalGroup");
  };

  const handleMenuOpen = (event, row) => {
    setMenuAnchorEl(event.currentTarget);
    setMenuRow(row);
  };
  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setMenuRow(null);
  };
  const handleMenuViewDetails = (row) => {
    handleViewDetails(row?._id);
    handleMenuClose();
  };

  const resolvePendingRegistration = async (id, type) => {
    return axios.post(
      `${API_ENDPOINT}/event/approve-pending-td/${id}?type=${type}`,
      {},
      getHeader()
    );
  };

  const handleOpenApproveDialog = (row) => {
    setApproveTargetRow(row);
    setActionError(null);
    setApproveDialogOpen(true);
    handleMenuClose();
  };
  const handleCloseApproveDialog = () => {
    if (actionLoading) return;
    setApproveDialogOpen(false);
    setApproveTargetRow(null);
    setActionError(null);
  };

  const handleConfirmApprove = async () => {
    if (!approveTargetRow?._id) return;
    setActionLoading(true);
    setActionError(null);
    try {
      await resolvePendingRegistration(
        approveTargetRow._id,
        ACTION_TYPES.TRADE_DELEGATE
      );
      setApproveDialogOpen(false);
      setApproveTargetRow(null);
      setSuccessMessage(SUCCESS_MESSAGES[ACTION_TYPES.TRADE_DELEGATE]);
      setSuccessDialogOpen(true);
    } catch (err) {
      setActionError(getErrorResponse(err) || "Failed to approve delegate");
    } finally {
      setActionLoading(false);
    }
  };

  const handleOpenUpdateDialog = (row) => {
    setUpdateTargetRow(row);
    setSelectedUpdateType(null);
    setActionError(null);
    setUpdateDialogOpen(true);
    handleMenuClose();
  };
  const handleCloseUpdateDialog = () => {
    if (actionLoading) return;
    setUpdateDialogOpen(false);
    setUpdateTargetRow(null);
    setSelectedUpdateType(null);
    setActionError(null);
  };
  const handleConfirmUpdate = async () => {
    if (!updateTargetRow?._id || !selectedUpdateType) return;
    setActionLoading(true);
    setActionError(null);
    try {
      await resolvePendingRegistration(updateTargetRow._id, selectedUpdateType);
      setUpdateDialogOpen(false);
      setUpdateTargetRow(null);
      setSuccessMessage(SUCCESS_MESSAGES[selectedUpdateType]);
      setSuccessDialogOpen(true);
    } catch (err) {
      setActionError(getErrorResponse(err) || "Failed to update delegate");
    } finally {
      setActionLoading(false);
    }
  };

  // --- NEW: Handle Resend Email ---
  const handleResendEmail = async (id) => {
    showDialog({
      type: DIALOG_TYPES.CONFIRM_WARNING,
      title: "Please Confirm!",
      message: "Are you sure you want to Resend Email?",
      buttonText: "Yes, Resend",
      onConfirm: async () => {
        try {
          // Adjust the exact endpoint if required by your backend routing
          await axios.post(
            `${API_ENDPOINT}/event/resend-email/${id}`,
            {},
            getHeader()
          );
          setSuccessMessage("Email has been successfully resent.");
          setSuccessDialogOpen(true);
        } catch (err) {
          toast.error(getErrorResponse(err) || "Failed to resend email");
        }
      },
    });
  };

  const handleCloseSuccessDialog = () => {
    setSuccessDialogOpen(false);
    setSuccessMessage("");
    window.location.reload();
  };

  const rowCount = data?.data?.length || 0;
  const numSelectedOnPage =
    data?.data?.filter((r) =>
      selectedRows.some((selected) => selected._id === r._id)
    ).length || 0;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom>
          Manage Trade Delegates
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Review, approve, and manage delegate registrations.
        </Typography>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="delegate tabs"
        >
          <Tab
            label={
              <TabLabel
                label="Pending Approval"
                count={pendingCountData?.totalItems}
                color="warning"
              />
            }
          />
          <Tab
            label={
              <TabLabel
                label="Approved"
                count={approvedCountData?.totalItems}
                color="success"
              />
            }
          />
        </Tabs>
      </Box>

      {/* SEARCH AND ROLE FILTER BAR */}
      <Box
        sx={{
          display: "flex",
          mb: 3,
          gap: 2,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <TextField
          variant="outlined"
          placeholder="Search by Name, Email, Mobile..."
          size="small"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          sx={{ width: { xs: "100%", sm: 350 }, bgcolor: "background.paper" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={18} />
              </InputAdornment>
            ),
          }}
        />

        {/* ROLE FILTER (Only visible on Approved tab) */}
        {activeTab === 1 && (
          <FormControl
            size="small"
            sx={{ minWidth: 200, bgcolor: "background.paper" }}
          >
            <InputLabel id="role-filter-label">Filter by Role</InputLabel>
            <Select
              labelId="role-filter-label"
              value={roleFilter}
              label="Filter by Role"
              onChange={(e) => {
                setRoleFilter(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="all">All Roles</MenuItem>
              <MenuItem value={USER_ROLES.TRADE_DELEGATE}>
                Trade Delegate
              </MenuItem>
              <MenuItem value={USER_ROLES.SPECIAL_INVITEE}>
                Special Invitee
              </MenuItem>
              <MenuItem value={USER_ROLES.VISITOR}>Visitor</MenuItem>
            </Select>
          </FormControl>
        )}
      </Box>

      {isError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {getErrorResponse(error)}
        </Alert>
      )}

      {isLoading && !isError ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
          <MuPb />
        </Box>
      ) : (
        data &&
        !isError && (
          <Paper
            variant="outlined"
            sx={{ overflow: "hidden", borderRadius: 2 }}
          >
            {activeTab === 1 && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  px: 2,
                  py: 1.5,
                  borderBottom: 1,
                  borderColor: "divider",
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {selectedRows.length > 0
                    ? `${selectedRows.length} item(s) selected across all pages`
                    : "No items selected"}
                </Typography>

                <Stack direction="row" spacing={1.5}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleExport}
                    disabled={visitorExporting}
                    startIcon={
                      visitorExporting ? (
                        <CircularProgress size={16} />
                      ) : (
                        <Download size={18} />
                      )
                    }
                    sx={{ textTransform: "none", fontWeight: 600 }}
                  >
                    {/* Export {selectedRows.length > 0 ? "Selected" : "All"} */}
                    Export All
                  </Button>

                  {data.data?.length > 0 && (
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handlePrintSelected}
                      startIcon={<Printer size={18} />}
                      sx={{ textTransform: "none", fontWeight: 600 }}
                    >
                      Print {selectedRows.length > 0 ? "Selected" : "All Page"}
                    </Button>
                  )}
                </Stack>
              </Box>
            )}

            <TableContainer>
              <Table sx={{ minWidth: 650 }} aria-label="delegates table">
                <TableHead sx={{ bgcolor: "background.default" }}>
                  <TableRow>
                    {activeTab === 1 && (
                      <TableCell padding="checkbox">
                        <Checkbox
                          color="primary"
                          indeterminate={
                            numSelectedOnPage > 0 &&
                            numSelectedOnPage < rowCount
                          }
                          checked={
                            rowCount > 0 && numSelectedOnPage === rowCount
                          }
                          onChange={handleSelectAllClick}
                        />
                      </TableCell>
                    )}
                    <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Role / Type</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Company</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      Registered On
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.data?.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={activeTab === 1 ? 7 : 6}
                        align="center"
                        sx={{ py: 5 }}
                      >
                        <Typography color="text.secondary">
                          No delegates found in this category.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    data.data?.map((row) => {
                      const isItemSelected = isSelected(row._id);

                      return (
                        <TableRow
                          key={row._id}
                          hover
                          role="checkbox"
                          aria-checked={isItemSelected}
                          selected={isItemSelected}
                        >
                          {activeTab === 1 && (
                            <TableCell padding="checkbox">
                              <Checkbox
                                color="primary"
                                checked={isItemSelected}
                                onChange={(e) => handleClickCheckbox(e, row)}
                              />
                            </TableCell>
                          )}
                          <TableCell>{getDelegateName(row)}</TableCell>

                          {/* Display role badge */}
                          <TableCell>
                            <Chip
                              label={
                                row.role === "trade_delegate"
                                  ? "Trade Delegate"
                                  : row.role === "Special Invitee"
                                  ? "Special Invitee"
                                  : row.role || "Pending"
                              }
                              size="small"
                              variant="outlined"
                              color={
                                row.role === "Special Invitee"
                                  ? "secondary"
                                  : "default"
                              }
                            />
                          </TableCell>

                          <TableCell>
                            {(typeof row.companyName === "string"
                              ? row.companyName
                              : row.companyName?.value) ||
                              row.company ||
                              row.institute ||
                              row.institution ||
                              row.organization ||
                              "-"}
                          </TableCell>
                          <TableCell>{row.email || "N/A"}</TableCell>
                          <TableCell>
                            {row.createdOn
                              ? new Date(row.createdOn).toLocaleDateString(
                                  undefined,
                                  {
                                    year: "numeric",
                                    month: "short",
                                    day: "numeric",
                                  }
                                )
                              : "N/A"}
                          </TableCell>
                          <TableCell align="right">
                            {activeTab === 0 ? (
                              <Tooltip title="Actions">
                                <IconButton
                                  size="small"
                                  onClick={(e) => handleMenuOpen(e, row)}
                                  aria-label="delegate actions"
                                >
                                  <MoreVertical size={20} />
                                </IconButton>
                              </Tooltip>
                            ) : (
                              <Stack
                                direction="row"
                                spacing={0.5}
                                justifyContent="flex-end"
                              >
                                {/* Added Resend Email Action */}
                                {row.role === USER_ROLES.TRADE_DELEGATE && (
                                  <Tooltip title="Resend Email">
                                    <IconButton
                                      onClick={() => handleResendEmail(row._id)}
                                      color="info"
                                      size="small"
                                    >
                                      <Mail size={20} />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                <Tooltip title="Print Badge">
                                  <IconButton
                                    onClick={() => handlePrintBadge(row)}
                                    color="secondary"
                                    size="small"
                                  >
                                    <Printer size={20} />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="View Details">
                                  <IconButton
                                    onClick={() => handleViewDetails(row._id)}
                                    color="primary"
                                    size="small"
                                  >
                                    <Eye size={20} />
                                  </IconButton>
                                </Tooltip>
                              </Stack>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={data.totalItems || 0}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 20, 50, 100]}
            />
          </Paper>
        )
      )}

      {/* Popovers and Menus */}
      <Menu
        anchorEl={menuAnchorEl}
        open={isMenuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem onClick={() => handleOpenApproveDialog(menuRow)}>
          <ListItemIcon>
            <CheckCircle size={18} color="green" />
          </ListItemIcon>
          <ListItemText>Approve</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleOpenUpdateDialog(menuRow)}>
          <ListItemIcon>
            <Pencil size={18} />
          </ListItemIcon>
          <ListItemText>Update</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleMenuViewDetails(menuRow)}>
          <ListItemIcon>
            <Eye size={18} />
          </ListItemIcon>
          <ListItemText>View</ListItemText>
        </MenuItem>
      </Menu>

      {/* Approve Dialog */}
      <Dialog
        open={approveDialogOpen}
        onClose={handleCloseApproveDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Approve Registration</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: "#334155" }}>
            You're about to approve{" "}
            <strong>{getDelegateName(approveTargetRow)}</strong>'s registration
            as a <strong>Trade Delegate</strong>.
            <br />
            <br />
            On approval, a confirmation email will be sent to the delegate with
            an option to activate and log in to their account to connect with
            exhibitors.
          </DialogContentText>
          {actionError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {actionError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseApproveDialog} disabled={actionLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmApprove}
            disabled={actionLoading}
            startIcon={
              actionLoading ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <CheckCircle size={16} />
              )
            }
            sx={{
              textTransform: "none",
              fontWeight: 600,
              backgroundColor: "#16a34a",
              "&:hover": { backgroundColor: "#15803d" },
            }}
          >
            {actionLoading ? "Approving..." : "Approve"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Update Dialog */}
      <Dialog
        open={updateDialogOpen}
        onClose={handleCloseUpdateDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          Register {getDelegateName(updateTargetRow)} As
        </DialogTitle>
        <DialogContent>
          <Stack direction="row" spacing={1.5} sx={{ mt: 0.5, mb: 2 }}>
            {Object.entries(UPDATE_TYPE_INFO).map(([typeKey, info]) => {
              const isSelected = selectedUpdateType === typeKey;
              return (
                <Button
                  key={typeKey}
                  onClick={() => setSelectedUpdateType(typeKey)}
                  variant={isSelected ? "contained" : "outlined"}
                  startIcon={info.icon}
                  fullWidth
                  disabled={actionLoading}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    py: 1.25,
                    borderRadius: 2,
                  }}
                >
                  {info.label}
                </Button>
              );
            })}
          </Stack>
          {selectedUpdateType ? (
            <Alert severity="info" sx={{ mb: 1 }}>
              {UPDATE_TYPE_INFO[selectedUpdateType].description}
            </Alert>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Choose how this registration should be handled.
            </Typography>
          )}
          {actionError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {actionError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseUpdateDialog} disabled={actionLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmUpdate}
            disabled={!selectedUpdateType || actionLoading}
            startIcon={
              actionLoading ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <CheckCircle size={16} />
              )
            }
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            {actionLoading ? "Saving..." : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Dialog */}
      <Dialog
        open={successDialogOpen}
        onClose={handleCloseSuccessDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            fontWeight: 700,
            color: "#16a34a",
          }}
        >
          <CheckCircle size={26} /> Done
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: "#334155" }}>
            {successMessage}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCloseSuccessDialog}
            variant="contained"
            disableElevation
            sx={{
              backgroundColor: "#16a34a",
              "&:hover": { backgroundColor: "#15803d" },
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 2,
              px: 3,
            }}
          >
            Understood
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ManageDelegates;
